import { formatEtaSeconds } from "./clientHygieneEta";
import { formatDashboardPeriodLabel } from "./dashboardDeepLink";

const ROUTE_LABELS = {
  "atendimento:summary": "Visão Geral",
  "atendimento:relacionamento:summary": "Atendimento",
  "financeiro-atendimento:summary": "Custos",
  "clientes:charts:summary": "Clientes",
  "clientes:charts:all": "Clientes",
  "clientes:charts:funnel": "Clientes",
  "clientes:charts:awareness_funnel": "Clientes",
  "clientes:charts:trend": "Clientes",
  "produtos:charts": "Produtos",
  "vendas:charts": "Vendas",
  "venda-passagens:charts": "Passagens",
  "origem:summary": "Campanhas",
  "partner:atendimento:summary": "Visão Geral",
  "partner:clientes:charts": "Clientes",
  "partner:produtos:charts": "Produtos",
  "partner:vendas:charts": "Vendas",
  "partner:venda-passagens:charts": "Passagens",
  "partner:surveys:analytics": "Pesquisas",
};

const QUEUED_GRACE_MS = 12_000;
const MIN_REMAINING_MS = 8_000;
const RUN_BOOTSTRAP_MS = 2_000;
const MAX_INFLIGHT_PROGRESS = 94;
const QUEUED_BASE_PROGRESS = 6;
const DEFAULT_ESTIMATE_MS = 120_000;

export function dashboardRouteLabel(route) {
  if (!route) return "Dashboard";
  if (ROUTE_LABELS[route]) return ROUTE_LABELS[route];
  if (String(route).startsWith("surveys:analytics:")) return "Pesquisas";
  if (String(route).startsWith("produtos:charts")) return "Produtos";
  if (String(route).startsWith("vendas:charts")) return "Vendas";
  if (String(route).startsWith("venda-passagens:charts")) return "Passagens";
  if (String(route).startsWith("origem:summary")) return "Campanhas";
  if (String(route).startsWith("atendimento:relacionamento:")) return "Atendimento";
  if (String(route).startsWith("financeiro-atendimento:summary")) return "Custos";
  return "Dashboard";
}

/** Strips notification-ready wording from titles stored at job enqueue time. */
export function sanitizeBackgroundJobTitle(rawTitle, route) {
  let title = String(rawTitle || "").trim();
  title = title.replace(/\s*[—–-]\s*relat[oó]rio pronto\s*$/i, "");
  title = title.replace(/\s*[—–-]\s*report ready\s*$/i, "");
  title = title.replace(/^pesquisa pronta:\s*/i, "");
  if (!title || /relat[oó]rio pronto|report ready/i.test(title)) {
    title = dashboardRouteLabel(route);
  }
  return title;
}

export function formatBackgroundJobSubtitle(job, locale = "pt-BR") {
  const formatted = formatDashboardPeriodLabel(job?.start, job?.end, locale);
  if (formatted) return formatted;

  const body = String(job?.body || "").trim();
  if (!body) return "";
  return body
    .replace(/\s*·\s*Válido até\s+.+$/i, "")
    .replace(/\s*·\s*Valid until\s+.+$/i, "")
    .replace(/\s*·\s*aba\s+.+$/i, "")
    .replace(/^Período\s+/i, "")
    .trim();
}

export function resolveBackgroundJobStatus(job) {
  const status = String(job?.status || "queued");
  const progress = Number(job?.progressPercent) || 0;
  const elapsedMs = Number(job?.elapsedMs) || 0;

  if (status === "running" || progress >= 14 || elapsedMs >= QUEUED_GRACE_MS) {
    return "generating";
  }
  if (status === "queued") return "starting";
  return "generating";
}

export function formatDurationMs(ms) {
  const sec = Math.max(0, Math.round(Number(ms || 0) / 1000));
  return formatEtaSeconds(sec);
}

function resolveEffectiveStatus(job, elapsedMs) {
  let status = String(job?.status || "queued");
  if (status === "computing") status = "running";
  if (status === "queued" && elapsedMs >= QUEUED_GRACE_MS) status = "running";
  return status;
}

function resolveRunElapsedMs(job, elapsedMs, nowMs) {
  const startedAtMs = job?.startedAt ? new Date(job.startedAt).getTime() : null;
  if (startedAtMs != null) return Math.max(0, nowMs - startedAtMs);
  return Math.max(0, elapsedMs - RUN_BOOTSTRAP_MS);
}

function resolveEffectiveTotalMs(baseEstimate, runElapsedMs) {
  if (runElapsedMs <= baseEstimate * 0.88) return baseEstimate;
  // Headroom ~12% (não 28%): evita barra travada em ~72% quando o job passa da estimativa.
  const targetRatio = Math.min(0.92, MAX_INFLIGHT_PROGRESS / 100);
  const projected = Math.ceil(runElapsedMs / targetRatio);
  const maxCap = Math.max(baseEstimate * 4, 2_400_000);
  return Math.min(Math.max(baseEstimate, projected), maxCap);
}

function computeProgressPercent(effectiveStatus, elapsedMs, runElapsedMs, effectiveTotalMs) {
  if (effectiveStatus === "queued") {
    return Math.round(QUEUED_BASE_PROGRESS + Math.min(7, (elapsedMs / 4000) * 7));
  }
  const raw = (runElapsedMs / effectiveTotalMs) * 100;
  const floor = elapsedMs >= QUEUED_GRACE_MS ? 14 : 12;
  return Math.round(Math.max(floor, Math.min(MAX_INFLIGHT_PROGRESS, raw)));
}

function computeRemainingMs(runElapsedMs, effectiveTotalMs, progressPercent) {
  const linear = effectiveTotalMs - runElapsedMs;
  if (linear >= MIN_REMAINING_MS) return linear;

  if (progressPercent > 0 && progressPercent < MAX_INFLIGHT_PROGRESS) {
    const fromProgress = Math.ceil((runElapsedMs * (100 - progressPercent)) / progressPercent);
    return Math.max(MIN_REMAINING_MS, fromProgress);
  }

  return MIN_REMAINING_MS;
}

function earliestIsoDate(a, b) {
  if (!a) return b || null;
  if (!b) return a || null;
  return new Date(a).getTime() <= new Date(b).getTime() ? a : b;
}

/** Smooth client-side progress between API polls. */
export function refreshDashboardJobProgress(job, nowMs = Date.now()) {
  if (!job) return job;
  if (job.status === "ready" || job.progressPercent >= 100) {
    return { ...job, progressPercent: 100, estimatedRemainingMs: 0 };
  }

  const createdAtMs = job.createdAt ? new Date(job.createdAt).getTime() : nowMs;
  const prevMaxElapsed = Number(job._maxElapsedMs) || 0;
  const computedElapsed = Math.max(0, nowMs - createdAtMs);
  const elapsedMs = Math.max(prevMaxElapsed, computedElapsed);

  const baseEstimate = Number(job.estimatedTotalMs) || DEFAULT_ESTIMATE_MS;
  const effectiveStatus = resolveEffectiveStatus(job, elapsedMs);
  const runElapsedMs = resolveRunElapsedMs(job, elapsedMs, nowMs);
  const effectiveTotalMs = resolveEffectiveTotalMs(baseEstimate, runElapsedMs);
  const progressPercent = computeProgressPercent(
    effectiveStatus,
    elapsedMs,
    runElapsedMs,
    effectiveTotalMs
  );
  const estimatedRemainingMs = computeRemainingMs(runElapsedMs, effectiveTotalMs, progressPercent);

  return {
    ...job,
    elapsedMs,
    _maxElapsedMs: elapsedMs,
    progressPercent,
    estimatedTotalMs: effectiveTotalMs,
    estimatedRemainingMs,
  };
}

export function buildLocalDashboardJob(meta, { start, end, title, body, deepLink } = {}) {
  const now = Date.now();
  const route = meta?.route || null;
  return refreshDashboardJobProgress(
    {
      jobId: meta.jobId,
      status: meta.status === "running" ? "running" : "queued",
      route,
      title: title || dashboardRouteLabel(route),
      body: body || null,
      deepLink: deepLink || null,
      start: start || null,
      end: end || null,
      estimatedRows: meta.estimatedRows || 0,
      createdAt: new Date(now).toISOString(),
      startedAt: null,
      progressPercent: meta.progressPercent || 8,
      elapsedMs: 0,
      _maxElapsedMs: 0,
      estimatedTotalMs: meta.estimatedTotalMs || DEFAULT_ESTIMATE_MS,
      estimatedRemainingMs: meta.estimatedRemainingMs || meta.estimatedTotalMs || DEFAULT_ESTIMATE_MS,
    },
    now
  );
}

export function mergeDashboardJobRecords(apiJob, localJob) {
  if (!localJob) return apiJob;
  if (!apiJob) return localJob;

  const createdAt = earliestIsoDate(apiJob.createdAt, localJob.createdAt);
  const maxElapsed = Math.max(Number(apiJob._maxElapsedMs) || 0, Number(localJob._maxElapsedMs) || 0);

  return {
    ...apiJob,
    createdAt: createdAt || apiJob.createdAt,
    _maxElapsedMs: maxElapsed,
    estimatedTotalMs: Number(apiJob.estimatedTotalMs) || Number(localJob.estimatedTotalMs) || DEFAULT_ESTIMATE_MS,
  };
}
